import { type ColorToken, formatCurrency } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CompareFactor,
  ComparedCollege,
  ComparisonSettings,
  FitStep,
  PREFERENCE_OPTIONS,
  PreferenceFactor,
  PreferenceOption,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

const STRINGS = appStrings.modules.myColleges.compare;

export const FIT_LEVELS = ['strong', 'mid', 'weak'] as const;
type FitLevel = (typeof FIT_LEVELS)[number];

export const FIT_LEVEL_FILLS: Record<FitLevel, ColorToken> = {
  strong: 'Brand/200',
  mid: 'Brand/100',
  weak: 'Brand/50',
};

export const fitLevel = (fit: FitStep): FitLevel =>
  fit >= 4 ? 'strong' : fit === 3 ? 'mid' : 'weak';

export const fitFill = (fit: FitStep | null): ColorToken =>
  fit === null ? 'Colors/Background/bg-primary' : FIT_LEVEL_FILLS[fitLevel(fit)];

export const factorLabel = (factor: CompareFactor) => STRINGS.factors[factor].label;

const thousands = (value: number) =>
  `${value < 0 ? '-' : ''}$${Math.abs(Math.round(value / 1000))}k`;

export const factFor = (college: ComparedCollege, factor: CompareFactor): string | null => {
  const { value } = college.factors[factor];

  if (value === null) return null;
  if (typeof value === 'string') return STRINGS.optionLabels[value as PreferenceOption] ?? value;

  switch (factor) {
    case 'net_cost':
      return college.net_cost_source === 'average'
        ? `${formatCurrency(value)} ${STRINGS.netCostAverage}`
        : formatCurrency(value);
    case 'graduation_rate':
      return `${Math.round(value * 100)}%`;
    case 'median_earnings':
    case 'roi_10_year':
      return thousands(value);
    case 'distance':
      return STRINGS.miles(value.toLocaleString('en-US'));
    default:
      return String(value);
  }
};

export const isEstimate = (college: ComparedCollege, factor: CompareFactor) =>
  factor === 'net_cost' && college.net_cost_source === 'scorecard';

export const percent = (fraction: number) => Math.round(fraction * 100);

export const rankedByMatch = (colleges: readonly ComparedCollege[]) =>
  colleges
    .filter((college) => college.match !== null)
    .sort((a, b) => (b.match ?? 0) - (a.match ?? 0));

export const orderedByMatch = (colleges: readonly ComparedCollege[]) => [
  ...rankedByMatch(colleges),
  ...colleges.filter((college) => college.match === null),
];

export const topMatchId = (colleges: readonly ComparedCollege[]) =>
  rankedByMatch(colleges)[0]?.institution_id ?? null;

export const FITS_BY_RANK: Record<number, readonly number[]> = {
  2: [5, 1],
  3: [5, 3, 1],
  4: [5, 4, 2, 1],
};

export const rankedOptions = (settings: ComparisonSettings, factor: PreferenceFactor) => {
  const fits: Readonly<Record<string, number>> = settings.preferences[factor];

  return [...PREFERENCE_OPTIONS[factor]].sort((a, b) => fits[b] - fits[a]) as PreferenceOption[];
};

export const withRanking = (
  settings: ComparisonSettings,
  factor: PreferenceFactor,
  order: readonly PreferenceOption[],
): ComparisonSettings => {
  const fits = FITS_BY_RANK[order.length];

  return {
    ...settings,
    preferences: {
      ...settings.preferences,
      [factor]: Object.fromEntries(order.map((option, rank) => [option, fits[rank]])),
    },
  };
};
